import { useEffect, useState } from 'react';
import type { UploadProgress } from '../lib/api';
import { Lightbox } from './Lightbox';

export type StagedFileState =
  | { kind: 'pending' }
  | { kind: 'uploading'; progress: UploadProgress }
  | { kind: 'done'; url: string }
  | { kind: 'error'; message: string };

export interface StagedFile {
  id: string;
  file: File;
  state: StagedFileState;
}

interface FilePreviewProps {
  staged: StagedFile;
  onCancel: (id: string) => void;
}

export function FilePreview({ staged, onCancel }: FilePreviewProps) {
  const { id, file, state } = staged;
  const [localUrl, setLocalUrl] = useState('');
  const [imageBroken, setImageBroken] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const isImage = file.type.startsWith('image/');
  const fileUrl = state.kind === 'done' && state.url ? state.url : localUrl;
  const percent =
    state.kind === 'uploading' && state.progress.total > 0
      ? Math.round((state.progress.loaded / state.progress.total) * 100)
      : 0;

  useEffect(() => {
    if (state.kind === 'done' && state.url) {
      setLocalUrl('');
      return;
    }
    if (typeof URL === 'undefined' || !URL.createObjectURL) return;

    const url = URL.createObjectURL(file);
    setLocalUrl(url);
    return () => URL.revokeObjectURL?.(url);
  }, [file, state]);

  return (
    <div className="file-preview" data-state={state.kind}>
      <div className="file-preview-thumb">
        {isImage && fileUrl && !imageBroken ? (
          <button
            type="button"
            className="file-preview-image-button"
            onClick={() => setLightboxOpen(true)}
            aria-label={`Open ${file.name} preview`}
          >
            <img
              src={fileUrl}
              alt={file.name}
              className="file-preview-image"
              onError={() => setImageBroken(true)}
            />
          </button>
        ) : isImage ? (
          <span className="file-preview-fallback" role="status">
            Image preview unavailable
          </span>
        ) : (
          <a
            className="file-preview-download"
            href={fileUrl || '#'}
            download={file.name}
            aria-label={`Download ${file.name}`}
          >
            Download
          </a>
        )}
      </div>
      <div className="file-preview-meta">
        <span className="file-preview-name" title={file.name}>
          {file.name}
        </span>
        <span className="file-preview-size">{formatBytes(file.size)}</span>
      </div>
      {state.kind === 'uploading' && (
        <div
          className="file-preview-progress"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Uploading ${file.name}`}
        >
          <div
            className="file-preview-progress-fill"
            style={{ width: `${percent}%` }}
          />
          <span className="file-preview-progress-label">{percent}%</span>
        </div>
      )}
      {state.kind === 'error' && (
        <span className="file-preview-error" role="alert">
          {state.message}
        </span>
      )}
      <button
        type="button"
        className="file-preview-cancel"
        onClick={() => onCancel(id)}
        aria-label={`Remove ${file.name}`}
      >
        ×
      </button>
      {lightboxOpen && fileUrl && (
        <Lightbox
          src={fileUrl}
          alt={file.name}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
